import { inject, Service } from "@angular/core";
import { CheckInStatusEnum } from "app/shared/enums/check-in-status-type.enum";
import { LocationTypeEnum } from "app/shared/enums/location-type.enum";
import { RolesEnum } from "app/shared/enums/roles.enum";
import { HttpBasicRequest } from "app/shared/models/http-basic-request.model";
import { PagedResponse } from "app/shared/models/paged-response.model";
import { BasicHttpClientService } from "app/shared/services/basic-http-client.service";
import { UtilityFunctions } from "app/shared/utilities/functions.models";
import { environment } from "environments/environment";
import { Observable, of, Subject } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";

import { ContactVM } from "../../models/back-office-admin-common/contact.models";
import { CustomerFullDTO } from "../../models/customer/customer.model";
import {
  LocationBaseDTO,
  LocationFullDTO,
} from "../../../../shared/models/location.model";
import { PointOfSaleDetailVM } from "../../models/point-of-sale/bo-point-of-sale.model";
import { ShippingAddressVM } from "../../models/point-of-sale/shipping-address.model";
import { ContactCRUD } from "../../utilities/contact-crud";
import { LocationMapper } from "../../utilities/location-mapper";
import { SessionStore } from "app/shared/state/session.store";
import { ShopContextStore } from "app/shared/state/shop-context.store";
import { PointOfSaleDto } from "app/shared/models/point-of-sale.model";
import { StorePosRequestModel } from "../../models/point-of-sale/store-pos-request.model";


export interface GetPointOfSalesParams {
  skipCount: number;
  maxResultCount: number;
  includeLocation: boolean;
  storeId?: number;
  sorting?: string;
  search?: string;
  city?: string;
  tierId?: number;
  customerId?: number;
  locationType?: number;
  checkInStatus?: CheckInStatusEnum[];
  includeStorePointOfSales?: boolean;
  whitoutStore?: boolean;
  customerIds?: number[];
}

@Service()
export class PointOfSaleService {
  private _httpClient = inject(BasicHttpClientService);
  private _session = inject(SessionStore);
  private _shopContext = inject(ShopContextStore);

  private _baseUrl: string = environment.urls.apiUrl.url;

  getShippingLocation(
    loaderId: string,
    customerId: number,
    skipCount?: number,
    maxResultCount?: number,
    storeId: number = null,
    whitoutStore?: boolean,
    checkInStatus?: CheckInStatusEnum,
  ) {
    const params = {
      skipCount: skipCount,
      maxResultCount: maxResultCount,
      type: LocationTypeEnum.ShippingLocation,
      whitoutStore: whitoutStore ?? false,
    };
    if (storeId) params["storeId"] = storeId;
    if (customerId) params["customerId"] = customerId;
    if (checkInStatus) params["checkInStatus"] = checkInStatus;
    return this._httpClient.get<PagedResponse<LocationFullDTO>>(
      this._baseUrl + `/locations`,
      { params },
      loaderId,
    );
  }

  getPointOfSales(loaderId: string, filters: GetPointOfSalesParams): Observable<PagedResponse<PointOfSaleDetailVM>> {
    const {
      skipCount,
      maxResultCount,
      includeLocation,
      storeId,
      sorting,
      search,
      city,
      tierId,
      customerId,
      locationType,
      checkInStatus,
      includeStorePointOfSales,
      whitoutStore,
      customerIds,
    } = filters;

    const params = {
      skipCount,
      maxResultCount,
      includeLocation: includeLocation || false,
      IncludeStorePointOfSales: includeStorePointOfSales || false,
      whitoutStore: whitoutStore ?? false,
      sorting: sorting || "name",
    };

    if (storeId) params["storeId"] = storeId;
    if (search) params["searchString"] = search;
    if (city) params["city"] = city;
    if (tierId) params["tierId"] = tierId;
    if (customerId) params["customerId"] = customerId;
    if (locationType) params["locationType"] = locationType;
    if (checkInStatus) params["checkInStatus"] = checkInStatus;
    if (customerIds) params["customerIds"] = customerIds;

    return this._httpClient
      .get<PagedResponse<PointOfSaleDto>>(
        this._baseUrl + `/point-of-sales`,
        {
          params,
        },
        loaderId,
      )
      .pipe(
        map(
          (data) =>
            new PagedResponse<PointOfSaleDetailVM>({
              items: data.items.map((x) =>
                LocationMapper.PointOfSaleDetail(
                  x,
                  [],
                  this._shopContext.shops(),
                  this._shopContext.currentShop().id,
                  null,
                  this._session.userInfo().mainRole,
                ),
              ),
              totalCount: data.totalCount,
            }),
        ),

        catchError((error) => {
          console.error(error);
          return of({
            totalCount: 0,
            items: [],
          });
        }),
      );
  }

  getAllLocation(
    loaderId: string,
    search?: string,
  ): Observable<PagedResponse<LocationFullDTO>> {
    const params = {
      skipCount: 0,
      maxResultCount: 53,
    };

    if (search) params["searchString"] = search;

    return this._httpClient.get<PagedResponse<LocationFullDTO>>(
      this._baseUrl + `/locations`,
      { params },
      loaderId,
    );
  }

  getPointOFSaleDetail(loaderId: string, pointOfSaleId: number) {
    const subject = new Subject<PointOfSaleDetailVM>();
    const storeId = this._shopContext.currentShop().id;
    // Era un subscribe su `shops` (BehaviorSubject): emetteva subito e poi a ogni setShops,
    // rilanciando le HTTP. Qui il valore serve una volta sola, alla chiamata.
    const shopList = this._shopContext.shops();
    this._httpClient
      .forkJoin<any>(
        [
          {
            url: this._baseUrl + `/point-of-sales/${pointOfSaleId}`,
            method: "get",
          },
          {
            url:
              this._baseUrl + `/point-of-sales/${pointOfSaleId}/contacts-pos`,
            method: "get",
          },
        ],
        loaderId,
      )
      .subscribe((res) => {
        const pointOfSale: PointOfSaleDto = res[0];
        const contact: ContactVM[] = res[1];
        this._httpClient
          .get(this._baseUrl + "/customers/" + pointOfSale.customerId)
          .subscribe((data: CustomerFullDTO) => {
            const pointOfSaleDetail: PointOfSaleDetailVM =
              LocationMapper.PointOfSaleDetail(
                pointOfSale,
                contact,
                shopList,
                storeId,
                data,
                this._session.userInfo().mainRole,
              );
            subject.next(pointOfSaleDetail);
          });
      });
    return subject;
  }

  getContactsPos(
    loaderId: string,
    pointOfSaleId: number,
  ): Observable<ContactVM[]> {
    return this._httpClient.get<ContactVM[]>(
      `${this._baseUrl}/point-of-sales/${pointOfSaleId}/contacts-pos`,
      {},
      loaderId,
    );
  }

  resendStorePos(
    loaderId: string,
    posData: PointOfSaleDetailVM,
  ): Observable<any> {
    var requests = posData.onlineStores
      .filter((x) => x.checkInStatus === CheckInStatusEnum.OnHold)
      .map(
        (x) =>
          new HttpBasicRequest({
            url:
              this._baseUrl +
              `/point-of-sales/${posData.id}/store/${x.store.id}`,
            method: "put",
            body: {
              tierId: x.tierId,
              checkInStatus: CheckInStatusEnum.OnHold,
              channels: x.channels.map((x) => x.id),
            },
          }),
      );
    requests.push(
      new HttpBasicRequest({
        url: this._baseUrl + "/point-of-sales/" + posData.id,
        method: "put",
        body: {
          id: posData.id,
          name: posData.name,
          externalId: posData.externalId,
          locationId: posData.locationId,
          summerStockLimit: posData.summerStockLimit,
          winterStockLimit: posData.winterStockLimit,
          summerStartMonth: posData.summerStartMonth,
          winterStartMonth: posData.winterStartMonth,
          summerStartDay: posData.summerStartDay,
          winterStartDay: posData.winterStartDay,
          useNameInOrderHeader: posData.useNameInOrderHeader,
        },
      }),
    );
    return this._httpClient.forkJoin(requests, loaderId);
  }

  approveStorePos(
    loaderId: string,
    posData: PointOfSaleDetailVM,
  ): Observable<any> {
    var requests = posData.onlineStores
      .filter((x) => x.checkInStatus === CheckInStatusEnum.OnHold)
      .map(
        (x) =>
          new HttpBasicRequest({
            url:
              this._baseUrl +
              `/point-of-sales/${posData.id}/store/${x.store.id}`,
            method: "put",
            body: {
              tierId: x.tierId,
              checkInStatus: CheckInStatusEnum.Approved,
              channels: x.channels.map((x) => x.id),
            },
          }),
      );
    requests.push(
      new HttpBasicRequest({
        url: this._baseUrl + "/point-of-sales/" + posData.id,
        method: "put",
        body: {
          id: posData.id,
          name: posData.name,
          externalId: posData.externalId,
          locationId: posData.locationId,
          summerStockLimit: posData.summerStockLimit,
          winterStockLimit: posData.winterStockLimit,
          summerStartMonth: posData.summerStartMonth,
          winterStartMonth: posData.winterStartMonth,
          summerStartDay: posData.summerStartDay,
          winterStartDay: posData.winterStartDay,
          useNameInOrderHeader: posData.useNameInOrderHeader,
        },
      }),
    );
    return this._httpClient.forkJoin(requests, loaderId);
  }

  updateStorePos(
    loaderId: string,
    posDataVM: PointOfSaleDetailVM,
    cachePosDataVm: PointOfSaleDetailVM,
    myCheckinStatus: CheckInStatusEnum,
  ): Observable<any> {
    var requests = LocationMapper.EditStoreToPointOFSaleRequest(
      posDataVM,
      cachePosDataVm,
      this._baseUrl,
      false,
      this._session.userInfo()?.mainRole,
      myCheckinStatus,
    );
    return this._httpClient.forkJoin(requests, loaderId);
  }

  updatePointOfSale(
    loaderId: string,
    posDataVM: PointOfSaleDetailVM,
    cachePosDataVm: PointOfSaleDetailVM,
    customer: CustomerFullDTO,
  ) {
    const result = new Subject();
    let requests: HttpBasicRequest[] = [];
    const changeSensitiveData =
      !UtilityFunctions.isEquals(posDataVM.location, cachePosDataVm.location) ||
      posDataVM.name !== cachePosDataVm.name ||
      posDataVM.useNameInOrderHeader !== cachePosDataVm.useNameInOrderHeader;
    requests = requests.concat(
      LocationMapper.PostStorePos(
        posDataVM,
        cachePosDataVm,
        this._baseUrl,
        changeSensitiveData,
        this._session.userInfo()?.mainRole,
        CheckInStatusEnum.OnHold,
      ),
    );
    // const deleteStoreToPos: HttpBasicRequest[] = LocationMapper.DeleteStoreToPointOFSaleRequest(posDataVM, cachePosDataVm, this.baseUrl)
    if (!UtilityFunctions.isMatch(posDataVM.location, cachePosDataVm.location))
      requests.push({
        url: this._baseUrl + `/locations/${posDataVM.location.id}`,
        method: "put",
        body: posDataVM.location,
      });
    if (
      posDataVM.isFromBilling &&
      customer &&
      requests?.some(
        (r) =>
          Array.isArray(r.body) &&
          r.body?.some(
            (x: StorePosRequestModel) =>
              x.checkInStatus === CheckInStatusEnum.OnHold,
          ),
      )
    ) {
      customer.sendToErp = true;
      customer.checkInStatus = CheckInStatusEnum.OnHold;
      requests.push({
        url: this._baseUrl + `/customers/${customer.id}`,
        method: "put",
        body: customer,
      });
    }

    this._httpClient
      .put(
        this._baseUrl + `/point-of-sales/${posDataVM.id}`,
        {
          id: posDataVM.id,
          name: posDataVM.name,
          alternativeVat: posDataVM.alternativeVat,
          alternativeGlnNumber: posDataVM.alternativeGlnNumber,
          externalId: posDataVM.externalId,
          locationId: posDataVM.locationId,
          summerStockLimit: posDataVM.summerStockLimit,
          winterStockLimit: posDataVM.winterStockLimit,
          summerStartMonth: posDataVM.summerStartMonth,
          winterStartMonth: posDataVM.winterStartMonth,
          summerStartDay: posDataVM.summerStartDay,
          winterStartDay: posDataVM.winterStartDay,
          useNameInOrderHeader: posDataVM.useNameInOrderHeader,
        },
        null,
        loaderId,
      )
      .subscribe((posRes) => {
        ContactCRUD.ContactCreateOrEditOrDelete(
          loaderId,
          this._httpClient,
          this._baseUrl,
          posDataVM.contacts,
          cachePosDataVm.contacts,
          "pos",
          posDataVM.id,
        )
          .pipe(filter((res) => !!res))
          .subscribe((contactRes) => {
            !requests.length
              ? result.next(posRes)
              : this._httpClient
                  .forkJoin(requests, loaderId)
                  .subscribe((res) => {
                    requests = LocationMapper.StorePosAgenciesHandler(
                      posDataVM.id,
                      posDataVM.onlineStores,
                      cachePosDataVm.onlineStores,
                      this._baseUrl,
                    );
                    !requests.length
                      ? result.next(posRes)
                      : this._httpClient
                          .forkJoin(requests, loaderId)
                          .subscribe((res) => result.next(posRes));
                  });
          });
      });
    return result.asObservable();
  }

  createPointOfSale(
    loaderId: string,
    posData: PointOfSaleDetailVM,
    customerId: number,
  ) {
    const result = new Subject();
    const promise = new Promise((resolve, reject) => {
      if (posData.useCustomerBillingLocation) {
        resolve(posData.location);
      } else {
        this._httpClient
          .post(this._baseUrl + `/locations`, posData.location, {}, loaderId)
          .subscribe((res: LocationFullDTO) => {
            resolve(res);
          });
      }
    }).then((location: LocationFullDTO) => {
      const locationId = location.id;
      const pointOfSale: HttpBasicRequest[] = LocationMapper.AddPointOfSale(
        customerId,
        locationId,
        posData,
        this._baseUrl,
      );
      pointOfSale.push(
        new HttpBasicRequest({
          method: "get",
          url: this._baseUrl + `/customers/${customerId}`,
        }),
      );
      this._httpClient
        .forkJoin(pointOfSale, loaderId)
        .subscribe((res2: any) => {
          const posId = res2[0]?.id;
          const customer: CustomerFullDTO = res2[1];
          ContactCRUD.ContactCreateOrEditOrDelete(
            loaderId,
            this._httpClient,
            this._baseUrl,
            posData.contacts,
            [],
            "pos",
            posId,
          )
            .pipe(filter((res) => !!res))
            .subscribe(() => {
              posData.id = posId;
              let requests: HttpBasicRequest[] = LocationMapper.PostStorePos(
                posData,
                null,
                this._baseUrl,
                true,
                this._session.userInfo()?.mainRole,
                CheckInStatusEnum.OnHold,
              );
              requests?.length
                ? this._httpClient
                    .forkJoin(requests, loaderId)
                    .subscribe(() => {
                      requests = LocationMapper.StorePosAgenciesHandler(
                        posId,
                        posData.onlineStores,
                        [],
                        this._baseUrl,
                      );
                      if (
                        posData.useCustomerBillingLocation &&
                        customer &&
                        posData.onlineStores.some(
                          (os) => os.checkInStatus == CheckInStatusEnum.OnHold,
                        )
                      ) {
                        customer.sendToErp = true;
                        customer.checkInStatus = CheckInStatusEnum.OnHold;
                        requests.push({
                          url: this._baseUrl + `/customers/${customer.id}`,
                          method: "put",
                          body: customer,
                        });
                      }
                      this._httpClient
                        .forkJoin(requests, loaderId)
                        .subscribe(() => {
                          result.next(undefined);
                        });
                      result.next(undefined);
                    })
                : result.next(undefined);
            });
        });
    });
    return result;
  }

  // updateLocation(loaderId: string, customerId: number, address: ShippingAddressVM, cachedAddress: ShippingAddressVM) {
  //   const result = new Subject<any>();
  //   const myCheckinStatus = this._session.userInfo()?.tenant?.id?.toLowerCase() === environment.tenants.find(t => t.tenantType === TenantTypeEnum.KappaSpain)?.id?.toLowerCase() ? CheckInStatusEnum.Approved : CheckInStatusEnum.OnHold;
  //   this._httpClient.put(this.baseUrl + `/locations/${address.location.id}`, address.location, {}, loaderId).subscribe(data => {
  //     let requests: HttpBasicRequest[] = [];
  //     requests = requests.concat(LocationMapper.AddStoreToCustomerLocationRequest(customerId, address, this.baseUrl, this._session.userInfo()?.mainRole, myCheckinStatus));
  //     requests = requests.concat(LocationMapper.EditStoreToCustomerLocationRequest(customerId, address, cachedAddress, this.baseUrl, this._session.userInfo()?.mainRole, myCheckinStatus));
  //     if (requests?.length)
  //       this._httpClient.forkJoin(requests, loaderId).subscribe(data => result.next(data ? data[0] as LocationFullDTO : null));
  //     else
  //       result.next(undefined);
  //   });
  //   return result.asObservable();
  // }

  createShippingAddres(
    loaderId: string,
    address: ShippingAddressVM,
    customerId: number,
  ) {
    const result = new Subject();
    this._httpClient
      .post(this._baseUrl + `/locations`, address.location, {}, loaderId)
      .subscribe((res) => {
        this._httpClient
          .post(
            this._baseUrl + `/customers/${customerId}/location`,
            {
              locationId: res.id,
              locationType: LocationTypeEnum.ShippingLocation,
            },
            {},
            loaderId,
          )
          .subscribe(() => {
            let requests: HttpBasicRequest[] = [];
            if (this._session.userInfo().mainRole <= RolesEnum.StoreUser) {
              requests = address.onlineStore
                ?.filter((x) => x.isEnabled)
                .map((so) => {
                  return new HttpBasicRequest({
                    url:
                      this._baseUrl +
                      "/customers/" +
                      customerId +
                      "/store-location/" +
                      res.id,
                    method: "post",
                    body: {
                      storeId: so.store.id,
                      checkInStatus: CheckInStatusEnum.OnHold,
                    },
                  });
                });
              if (requests?.length)
                this._httpClient
                  .forkJoin(requests, loaderId)
                  .subscribe(() => result.next(undefined));
              else result.next(undefined);
            } else {
              this._shopContext.shops().forEach((store) =>
                requests.push({
                  url:
                    this._baseUrl +
                    "/customers/" +
                    customerId +
                    "/store-location/" +
                    res.id,
                  method: "post",
                  body: {
                    storeId: store.id,
                    checkInStatus: CheckInStatusEnum.OnHold,
                  },
                }),
              );
              this._httpClient
                .forkJoin(requests, loaderId)
                .subscribe(() => result.next(undefined));
            }
          });
      });
    return result;
  }

  deletePointOfSale(loaderId: string, posId: number) {
    return this._httpClient.delete(
      this._baseUrl + `/point-of-sales/${posId}`,
      {},
      loaderId,
    );
  }

  deleteCustomerLocation(
    loaderId: string,
    customerId: number,
    locationId: number,
  ) {
    return this._httpClient.delete(
      this._baseUrl + `/customers/${customerId}/location/${locationId}`,
      {},
      loaderId,
    );
  }

  getCustomerBillingLocation(
    customerId: number,
    loaderId: string,
  ): Observable<LocationBaseDTO> {
    return this._httpClient
      .get<PagedResponse<LocationBaseDTO>>(
        `${this._baseUrl}/locations`,
        {
          params: { type: LocationTypeEnum.BillingLocation, customerId },
        },
        loaderId,
      )
      .pipe(
        map((data) => {
          return data?.items?.find((x) => x);
        }),
      );
  }
}
